import { ErrorMessage, Field, Form, Formik } from "formik";
import {
  BackButton,
  Box,
  ButtonStyled,
  LabelStyled,
  SubHeadingStyled,
} from "../../../../../components/styled/Elements";
import { useTranslation } from "react-i18next";
import { ProfileWrapper } from "./styled";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../../../components/admin/Breadcrumbs/Breadcrumbs";
import CabinetsApi from "../../../../../services/api/cabinetsApi";
import { toast } from "react-toastify";
import { ContentWrapper } from "../../../../../components/styled/styled";
import * as Yup from "yup";
import { getIsTemporary } from "../../../../../utils";
import { ValidatedInputWrapper } from "../../../../../components/common/inputFields/ValidatedInput";
import { FormGroup } from "react-bootstrap";
import AccessSettings from "../../components/AccessSettings";

const AddFile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isTemporary = getIsTemporary();

  const initialValues = {
    action: "create",
    is_temporary: isTemporary && isTemporary,
    name: "",
    description: "",
    setting_id: 1,
    my_department_id: null,
    my_dept_ids: [],
    other_department_id: null,
    other_dept_ids: [],
    referrals: [],
    from_branches: [],
    from_departments: [],
  };

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string().required("File name is required"),

      // 1. My Department ID
      // Required only if BOTH other_department_id AND setting_id are empty
      my_department_id: Yup.number()
        .nullable()
        .when(["other_department_id", "setting_id"], {
          is: (otherId, settingId) => !otherId && !settingId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      // 2. Other Department ID
      // Required only if BOTH my_department_id AND setting_id are empty
      other_department_id: Yup.number()
        .nullable()
        .when(["my_department_id", "setting_id"], {
          is: (myId, settingId) => !myId && !settingId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      // 3. Setting ID (Myself)
      // Required only if BOTH my_department_id AND other_department_id are empty
      setting_id: Yup.number()
        .nullable()
        .when(["my_department_id", "other_department_id"], {
          is: (myId, otherId) => !myId && !otherId,
          then: (schema) => schema.required(t("Pleaseselectone")),
          otherwise: (schema) => schema.notRequired(),
        }),

      // Baaki validations (ids selection) wahi rahengi
      my_dept_ids: Yup.array()
        .ensure()
        .when("my_department_id", {
          is: (val) => val === 4 || val === 5,
          then: (schema) =>
            schema
              .min(1, t("Please_select_user"))
              .required(t("Please_select_user")),
          otherwise: (schema) => schema.notRequired(),
        }),

      other_dept_ids: Yup.array()
        .ensure()
        .when("other_department_id", {
          is: (val) => [6, 7, 8, 9].includes(val),
          then: (schema) =>
            schema.min(1, t("Required")).required(t("Required")),
          otherwise: (schema) => schema.notRequired(),
        }),
    },
    [
      // Cyclic dependencies array
      ["my_department_id", "other_department_id"],
      ["my_department_id", "setting_id"],
      ["other_department_id", "setting_id"],
    ],
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      values.common_settings_id = values.setting_id ?? null;
      const response = await CabinetsApi.createCabinetFile(id, values);
      if (response.code === 200) {
        toast.success(t("file_created"));
        navigate(`/cabinets/${id}/files`);
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
      console.log("Error: ", error);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <ProfileWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("add_file")}
            onClick={() => navigate(`/cabinets/${id}/files`)}
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors }) => {
          return (
            <Form>
              <ContentWrapper>
                <Box>
                  <ValidatedInputWrapper>
                    <FormGroup className="formEleContainer">
                      <LabelStyled
                        className="border-0"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("file_name")}
                        <span className="staric">*</span>
                      </LabelStyled>

                      <Box className="w-100">
                        {/* Input Field */}
                        <Field
                          type="text"
                          name="name"
                          className="form-control"
                          placeholder="File Name"
                        />
                        {/* Error Message */}
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="text-danger text-sm mb-0 mt-1"
                        />
                      </Box>
                    </FormGroup>
                  </ValidatedInputWrapper>
                  <ValidatedInputWrapper>
                    <FormGroup className="formEleContainer">
                      <LabelStyled
                        className="border-0"
                        size="15px"
                        color="var(--text)"
                      >
                        {t("Description")}
                      </LabelStyled>

                      {/* Input Field */}
                      <Field
                        as="textarea"
                        type="textarea"
                        name="description"
                        className="form-control"
                        placeholder="Description"
                        style={{ height: "130px" }}
                      />

                      {/* Error Message */}
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="invalid-feedback d-block"
                      />
                    </FormGroup>
                  </ValidatedInputWrapper>
                </Box>
                <Box>
                  <Box className="innerFoHead">
                    <SubHeadingStyled size="16px" color="var(--text)">
                      {t("file_access_settings")}
                    </SubHeadingStyled>
                  </Box>

                  <AccessSettings showBaseCabinetOption={true}/>
                </Box>
                <Box className="d-flex justify-content-end">
                  <Box className="p-4 d-flex gap-3">
                    <ButtonStyled
                      label={t("Cancel")}
                      bg={"var(--bgSecondary)"}
                      color={"var(--text2)"}
                      type="button"
                      onClick={() => navigate("/cabinets")}
                    />
                    <ButtonStyled
                      label={t("Create")}
                      type="submit"
                      isLoading={isSubmitting}
                    />
                  </Box>
                </Box>
              </ContentWrapper>
            </Form>
          );
        }}
      </Formik>
    </ProfileWrapper>
  );
};

export default AddFile;
